import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  computed,
  inject,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { readBackendErrorCode } from '@app/shared/utils/error-handler.util';
import { PermissionService } from '@app/core/services/permission.service';

import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  RETAIL_STORE_SEARCH_CONFIG,
  RETAIL_STORE_COLUMNS,
  RETAIL_STORE_DEFAULT_COLUMNS_ORDER,
  RETAIL_STORE_FLEXIBLE_COLUMNS_ORDER,
  RETAIL_STORE_ACTION_COLUMN,
} from './retail-store.config';

// Import API service directly
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { PhysicalShopShowDTO } from '@app/api/channel-service/model/physicalShopShowDTO';
import { ShopDeleteDTO } from '@app/api/channel-service/model/shopDeleteDTO';

// TypeScript interfaces for better type safety
interface RetailStore {
  id: number;
  name: string;
  code: string;
  typeCode: string;
  address: string;
  manager: string;
  status: boolean;
  channelWarehouseStatus: boolean;
  channelWarehouseName: string;
  selfPickupFlag: boolean;
  salesOrganizingId?: number;
  invoiceTypeCode?: string;
  description?: string;
  countryId?: number;
  provinceId?: number;
  cityId?: number;
  countyId?: number;
  createdBy?: string;
  creationDate: string;
  lastUpdatedBy?: string;
  lastUpdateDate?: string;
  objectVersionNumber: number;
}

@Component({
  selector: 'app-retail-store',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './retail-store.component.html',
  styleUrls: ['./retail-store.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetailStoreComponent implements OnInit {
  private readonly storeHasAssociatedPosErrorCode = 'STORE_HAS_ASSOCIATED_POS';
  private readonly jsonAcceptOptions = {
    httpHeaderAccept: 'application/json' as '*/*',
  };

  // Search configuration
  searchConfig = RETAIL_STORE_SEARCH_CONFIG;

  // Table configuration (kept for reference, but using advanced table columns below)
  tableColumns = RETAIL_STORE_COLUMNS;

  // Button configuration base
  private readonly primaryButtonBase: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  private readonly secondaryButtonBase: ActionButton = {
    label: 'common.actions.delete',
    severity: 'danger',
    show: true,
    disabled: true,
    icon: 'delete',
  };

  // Computed button configurations with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Secondary button for batch delete - always visible if user has delete permission
  secondaryButton = computed(() => ({
    ...this.secondaryButtonBase,
    show: this.canDelete(), // Always show if user has delete permission
    disabled: !this.hasDeletableStores(), // Disable if no deletable items selected
  }));

  // Check if user has any action permissions
  hasAnyActionPermission = computed(
    () =>
      this.canUpdate() ||
      this.canDelete() ||
      this.canApprove() ||
      this.canView(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      RETAIL_STORE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of RETAIL_STORE_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(RETAIL_STORE_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      RETAIL_STORE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of RETAIL_STORE_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Data signals with proper typing
  retailStores = signal<RetailStore[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    name: '',
    typeCode: '',
    channelWarehouseStatus: undefined,
    manager: '',
    selfPickupFlag: undefined,
    status: undefined,
    page: 1,
    size: 20,
  });

  // Selection with proper typing
  selectedRetailStores = signal<RetailStore[]>([]);

  // Computed properties
  hasSelectedStores = computed(() => this.selectedRetailStores().length > 0);
  hasDeletableStores = computed(() => {
    const selected = this.selectedRetailStores();
    return selected.some((store) => !store.status);
  });
  hasActiveStores = computed(() =>
    this.selectedRetailStores().some((store) => store.status),
  );

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationMessage = signal('');
  confirmationTitle = signal('');
  confirmationButtons = signal<any[]>([]);
  confirmationAction = signal('');
  confirmationData = signal<any>(null);

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'retail_store' for channel permissions
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.retail_store.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.retail_store.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.retail_store.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.retail_store.approve');
  });
  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.retail_store.view');
  });

  constructor(
    private readonly physicalShopService: PhysicalShopControllerService,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadRetailStores();
  }

  /**
   * Load retail stores based on search parameters
   */
  loadRetailStores(): void {
    this.loading.set(true);

    // Combine search parameters and pagination into single DTO
    const searchDTO: PhysicalShopShowDTO = {
      name: this.searchParams().name || '',
      manager: this.searchParams().manager || '',
      channelWarehouseStatus: this.searchParams().channelWarehouseStatus,
      selfPickupFlag: this.searchParams().selfPickupFlag,
      status: this.searchParams().status,
      deleteFlag: false,
      pageRequest: {
        page: this.searchParams().page - 1, // API uses 0-based indexing
        size: this.searchParams().size,
        sortBy: 'creationDate',
        ascending: false,
      },
    };

    this.physicalShopService.showPage(searchDTO, 'body', false).subscribe({
      next: (response: any) => {
        // Handle blob response
        if (response instanceof Blob) {
          response
            .text()
            .then((text) => {
              try {
                const jsonData = JSON.parse(text);
                // Use real API data
                this.retailStores.set(
                  (jsonData.pageContent || []) as RetailStore[],
                );
                this.totalRecords.set(jsonData.totalElements || 0);

                this.loading.set(false);
              } catch (error) {
                console.error('Error parsing blob data:', error);
                this.loading.set(false);
              }
            })
            .catch((error) => {
              console.error('Error reading blob data:', error);
              this.loading.set(false);
            });
        } else {
          // Use real API data
          this.retailStores.set((response?.pageContent || []) as RetailStore[]);
          this.totalRecords.set(response?.totalElements || 0);

          this.loading.set(false);
        }
      },
      error: (error) => {
        console.error('Error loading retail stores:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'retailStore.messages.load_error',
          ),
        });
      },
    });
  }

  /**
   * Handle search form submission
   */
  onSearch(searchData: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      ...searchData,
      page: 1,
    });
    this.loadRetailStores();
  }

  /**
   * Handle search form reset
   */
  onReset(): void {
    this.searchParams.set({
      name: '',
      typeCode: '',
      status: undefined,
      channelWarehouseStatus: undefined,
      manager: '',
      selfPickupFlag: undefined,
      page: 1,
      size: 20,
    });
    this.selectedRetailStores.set([]);
    this.loadRetailStores();

    // Show success message
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('common.messages.info'),
      detail: this.translateService.instant(
        'retailStore.messages.search_reset',
      ),
    });
  }

  /**
   * Handle page change
   */
  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1,
    });
    this.loadRetailStores();
  }

  /**
   * Handle selection change
   */
  onSelectionChange(selection: RetailStore[]): void {
    this.selectedRetailStores.set(selection);
    // Button state is automatically updated via computed signal
    // secondaryButton computed signal depends on hasDeletableStores()
    // which depends on selectedRetailStores, so it will update automatically
  }

  /**
   * Navigate to add new store
   */
  onAdd(): void {
    this.router.navigate(['/channel/retail-store/add']);
  }

  /**
   * Navigate to edit store
   */
  onEdit(store: RetailStore): void {
    this.router.navigate(['/channel/retail-store/edit', store.id]);
  }

  /**
   * Navigate to view store
   */
  onView(store: RetailStore): void {
    this.router.navigate(['/channel/retail-store/view', store.id]);
  }

  /**
   * Handle status change
   */
  onStatusChange(store: RetailStore, newStatus: boolean): void {
    const action = newStatus ? 'enable' : 'disable';
    const message = this.translateService.instant(
      `retailStore.messages.confirm_${action}`,
      { name: store.name },
    );
    const title = this.translateService.instant(
      newStatus
        ? 'retailStore.dialog.activate_title'
        : 'retailStore.dialog.deactivate_title',
    );

    this.confirmationMessage.set(message);
    this.confirmationTitle.set(title);
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          newStatus
            ? 'retailStore.actions.activate'
            : 'retailStore.actions.deactivate',
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.confirmationAction.set('statusChange');
    this.confirmationData.set({ store, newStatus });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle delete store
   */
  onDelete(store: RetailStore): void {
    if (store.status) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'retailStore.messages.cannot_delete_enabled',
        ),
      });
      return;
    }

    this.confirmationMessage.set(
      this.translateService.instant('retailStore.messages.confirm_delete', {
        name: store.name,
      }),
    );
    this.confirmationTitle.set(
      this.translateService.instant('retailStore.dialog.delete_title'),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.confirmationAction.set('delete');
    this.confirmationData.set({ store });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle batch delete
   */
  onBatchDelete(): void {
    const selectedStores = this.selectedRetailStores();
    const activeStores = selectedStores.filter((store) => store.status);

    if (activeStores.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'retailStore.messages.cannot_delete_active_batch',
        ),
      });
      return;
    }

    this.confirmationMessage.set(
      this.translateService.instant(
        'retailStore.messages.confirm_batch_delete',
        {
          count: selectedStores.length,
        },
      ),
    );
    this.confirmationTitle.set(
      this.translateService.instant('retailStore.dialog.batch_delete_title'),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.confirmationAction.set('batchDelete');
    this.confirmationData.set({ selectedStores });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle confirmation dialog actions
   */
  onConfirmationDialogAction(action: string): void {
    this.showConfirmationDialog.set(false);

    if (action === 'confirm') {
      switch (this.confirmationAction()) {
        case 'statusChange':
          this.executeStatusChange(
            this.confirmationData().store,
            this.confirmationData().newStatus,
          );
          break;
        case 'delete':
          this.executeDelete(this.confirmationData().store);
          break;
        case 'batchDelete':
          this.executeBatchDelete(this.confirmationData().selectedStores);
          break;
      }
    }
  }

  /**
   * Execute status change
   */
  private executeStatusChange(store: RetailStore, newStatus: boolean): void {
    const action = newStatus ? 'enable' : 'disable';

    this.physicalShopService
      .operation(Number(store.id), newStatus, store.objectVersionNumber)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              `retailStore.messages.${action}_success`,
            ),
          });
          this.loadRetailStores();
        },
        error: (error) => {
          console.error(`Error ${action}ing store:`, error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              `retailStore.messages.${action}_error`,
            ),
          });
        },
      });
  }

  /**
   * Execute delete
   */
  private executeDelete(store: RetailStore): void {
    this.physicalShopService
      ._delete(
        Number(store.id),
        store.objectVersionNumber,
        'body',
        false,
        this.jsonAcceptOptions,
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'retailStore.messages.delete_success',
            ),
          });
          this.loadRetailStores();
        },
        error: (error) => {
          console.error('Error deleting store:', error);
          this.showDeleteError(error, 'retailStore.messages.delete_error');
        },
      });
  }

  /**
   * Execute batch delete
   */
  private executeBatchDelete(selectedStores: RetailStore[]): void {
    const deleteDTO: ShopDeleteDTO = {
      ids: selectedStores.map((store) => Number(store.id)),
    };

    this.physicalShopService
      .deletes(deleteDTO, 'body', false, this.jsonAcceptOptions)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'retailStore.messages.batch_delete_success',
            ),
          });
          this.selectedRetailStores.set([]);
          this.loadRetailStores();
        },
        error: (error) => {
          console.error('Error batch deleting stores:', error);
          this.showDeleteError(
            error,
            'retailStore.messages.batch_delete_error',
          );
        },
      });
  }

  private showDeleteError(error: unknown, fallbackDetailKey: string): void {
    const isAssociatedPosError =
      readBackendErrorCode(error) === this.storeHasAssociatedPosErrorCode;

    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant(
        isAssociatedPosError
          ? 'retailStore.messages.cannot_delete_store_title'
          : 'common.messages.error',
      ),
      detail: this.translateService.instant(
        isAssociatedPosError
          ? 'retailStore.messages.cannot_delete_associated_pos'
          : fallbackDetailKey,
      ),
    });
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(status: boolean): string {
    if (status) {
      return this.translateService.instant('common.table.status.active');
    }
    return this.translateService.instant('common.table.status.inactive');
  }

  /**
   * Get status severity
   */
  getStatusSeverity(status: boolean): string {
    if (status) {
      return 'success';
    }
    return 'danger';
  }

  /**
   * Get status style
   */
  getStatusStyle(status: boolean): any {
    return {
      '--status-color': status ? '#22c55e' : '#ef4444',
    };
  }

  /**
   * Get store type display text
   */
  getStoreTypeDisplayText(type: string): string {
    // Always show "General Shop" for all store types
    return 'General Shop';
  }

  /**
   * Get store type severity
   */
  getStoreTypeSeverity(type: string): string {
    const severityMap: { [key: string]: string } = {
      PHYSICAL: 'warning',
      ONLINE: 'info',
      HYBRID: 'success',
    };
    return severityMap[type] || 'info';
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
